import React, { useEffect, useState } from 'react'
import { fetchPageBySlug } from '../lib/cms'
import useMeta from '../hooks/useMeta'
import { Briefcase, CheckCircle2, Phone, Mail } from 'lucide-react'

export default function Job() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageBySlug('job')
      setPageData(data)
      setLoading(false)
    }
    loadData()
  }, [])

  // Set metadata
  useMeta({
    title: 'Job · JP Stål og Rør ApS',
    description: 'Lad os stå for dit næste projekt. Her kan du få et indblik i, hvad vi kan hjælpe dig med på dette område.',
    ogTitle: 'Job · JP Stål og Rør ApS',
    ogDescription: 'Lad os stå for dit næste projekt. Her kan du få et indblik i, hvad vi kan hjælpe dig med på dette område.',
    ogImage: 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d1a201a2-613d-4546-aa68-2fafe952f02e.jpeg',
    canonicalUrl: 'https://jpstaalogroer.dk/job'
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-silver-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="bg-silver-200 text-slate-900 min-h-screen py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-amber-500/20 text-amber-700 rounded-sm mb-4">
            <Briefcase className="w-8 h-8" />
          </div>
          <span className="block text-xs font-bold uppercase tracking-widest text-amber-600">Karriere &amp; Samarbejde</span>
          <h1 className="text-4xl font-black text-slate-950 uppercase tracking-tight mt-2 mb-4">
            Job hos JP Stål og Rør ApS
          </h1>
          <p className="text-slate-700 text-sm max-w-xl mx-auto font-medium">
            Her kan du læse mere om vores værdier, kvalitet, service og hvordan vi samarbejder om projekter.
          </p>
        </div>

        {/* Verbatim Content Block */}
        <div className="bg-silver-100 border border-silver-400 p-8 md:p-12 rounded-sm shadow-xl space-y-8">
          
          <div className="space-y-4">
            <h2 className="text-2xl font-black text-slate-950 uppercase tracking-tight">
              Lad os klare opgaven for dig
            </h2>
            <h3 className="text-lg font-bold text-slate-800">
              Vi tilbyder en bred vifte af opgaver – store som små
            </h3>
            <p className="text-slate-700 text-sm leading-relaxed font-normal">
              Uanset om du skal have hjælp til det ene eller det andet, stræber vi altid efter at finde den bedste løsning til netop dig. Finder du ikke netop det, som du skal have hjælp til beskrevet her, så kontakt os endelig.
            </p>
          </div>

          <div className="border-t border-silver-300 pt-8 space-y-4">
            <h3 className="text-lg font-bold text-slate-800">
              Grundig kommunikation fra start
            </h3>
            <p className="text-slate-700 text-sm leading-relaxed font-normal">
              Vi har stor forståelse for, at vores kunders behov og ønsker kan variere, så vi tager altid en indledende snak med dig omkring netop dette. På den måde kan vi bedst muligt nå i mål med leveringen af en komplet og skræddersyet løsning. Vi spørger grundigt ind til dine forventninger til opgaven, så vi undgår misforståelse. Vi holder dig også gerne opdateret fra start til slut, hvis du ønsker det. Det er vigtigt for os, at vores kunder føler sig trygge gennem hele forløbet.
            </p>
          </div>

          <div className="border-t border-silver-300 pt-8 space-y-4">
            <h3 className="text-lg font-bold text-slate-800">
              Høj kvalitet og god service
            </h3>
            <p className="text-slate-700 text-sm leading-relaxed font-normal">
              Hos os kan du altid regne med god service og høj kvalitet. Vi går aldrig fra en opgave, før den 100% færdig, og før begge parter er tilfredse med resultatet. Vi overholder alle aftaler og deadlines, så hvis du er på udkig efter kvalitetsløsninger til tiden, så er det os, du skal kontakte.
            </p>
            <p className="text-slate-700 text-sm leading-relaxed font-normal">
              Vores medarbejdere har den rette faglige viden og kompetencerne til at levere et veludført stykke arbejde. Vi møder dig altid med et smil, og vi besvarer med glæde ethvert spørgsmål, som du måtte have i løbet af projektet.
            </p>
          </div>

          {/* Contact CTA */}
          <div className="border-t border-silver-300 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-sm font-bold text-slate-950 uppercase tracking-wider">Vil du høre mere om jobmuligheder?</p>
              <p className="text-xs text-slate-600 mt-1">Kontakt Jakob Petersen direkte for en uforpligtende snak.</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <a
                href="tel:+4524460485"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-xs px-5 py-3 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>Ring op</span>
              </a>
              <a
                href="mailto:jp@jpstaal.dk"
                className="inline-flex items-center gap-2 bg-silver-300 hover:bg-silver-400 text-slate-950 border border-silver-400 font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-sm transition-colors duration-200 cursor-pointer shadow-sm"
              >
                <Mail className="w-3.5 h-3.5 text-amber-600" />
                <span>Send e-mail</span>
              </a>
            </div>
          </div>

        </div>

      </div>
    </div>
  )
}
